import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { RouteErrorBoundary } from './ErrorBoundary';
import { useBasePath } from '../../lib/config/nav-helpers';
import { clsx } from 'clsx';
import { lazy, Suspense, useEffect, useRef, useState, useCallback } from 'react';
import { useTheme } from '../../lib/config/theme';
import { pageTitle } from '../../lib/utils/page-title';
import { useUserName } from '../../lib/hooks/use-user-name';
import { useMe, useCan, ROLE_LABEL } from '../../lib/hooks/use-me';
import { ScopePicker } from '../forms/ScopePicker';
import { WaypointMark } from '../brand/WaypointMark';
import { PlatformSwitcher } from './PlatformSwitcher';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { ImpersonationBanner } from './ImpersonationBanner';
import { DemoBanner } from './DemoBanner';
import { DataFreshnessBanner } from './DataFreshnessBanner';
import { IndustryCtaBanner } from './IndustryCtaBanner';
import { NotificationCenter } from '../feeds/NotificationCenter';
import { useCommandPalette } from './CommandPalette';
import { useKeyboardShortcuts } from '../../lib/hooks/use-keyboard-shortcuts';
import { useActiveSync } from '../../lib/hooks/use-active-sync';
import { ShortcutsHelp } from './ShortcutsHelp';
import { OnboardingWizard, ContinueSetupBanner } from '../OnboardingWizard';
import { standaloneHome } from '../../lib/platform-pin';
import { TrialBanner } from './TrialBanner';
import { ActivationNudge } from '../funnel/ActivationNudge';
import { TrialRecapModal } from './TrialRecapModal';
import { PaywallProvider } from './PaywallGate';
import { MobileBottomNav } from './MobileBottomNav';
import { useSpeechRecognition } from '../../lib/hooks/use-speech-recognition';
import { isFounderEmail } from '../../lib/founder-gate';
import { trackPageView } from '../../lib/track-interaction';

// Lazy-loaded panels — only fetched when first shown, keeping them out of the main bundle.
const ChatPanel = lazy(() => import('../ChatPanel').then(m => ({ default: m.ChatPanel })));
const CommandPalette = lazy(() => import('./CommandPalette').then(m => ({ default: m.CommandPalette })));
const VoiceOverlay = lazy(() => import('./VoiceOverlay').then(m => ({ default: m.VoiceOverlay })));

type HubRoute = { suffix: string; label: string; icon: string; requiresPermission?: 'manageSettings'; founderOnly?: boolean; hiddenForPartners?: boolean; children?: HubRoute[] };

export const HUB_ROUTES: readonly HubRoute[] = [
  // Owner nav: eight destinations. Maintenance surfaces (Data Health, Alerts,
  // Audit) live under Settings; Pipeline lives in the Sales hub (/dashboard/pipeline redirects there); Plans under Actions.
  // Not in nav on purpose (reachable by URL, self-gated):
  //   /kpis + /ops — founder-only internal tools (see BusinessOS/FOUNDER_DASHBOARDS.md)
  //   /trust      — security posture page; belongs on the public site
  //   /contracts  — no document ingestion exists yet, so the page cannot populate
  //   /intelligence/models — linked from the Intelligence page
  { suffix: '', label: 'Command Center', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1' },
  { suffix: '/revenue', label: 'Finance', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1' },
  { suffix: '/clients', label: 'Clients', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z' },
  // hiddenForPartners: not in a design-partner workspace's nav (me.features.partner); the route + API gate it too.
  { suffix: '/intelligence', label: 'Intelligence', hiddenForPartners: true, icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' },
  { suffix: '/marketing', label: 'Marketing', hiddenForPartners: true, icon: 'M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z' },
  { suffix: '/meetings', label: 'Meetings', icon: 'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z' },
  { suffix: '/actions', label: 'Actions', icon: 'M13 10V3L4 14h7v7l9-11h-7z', children: [
    { suffix: '/plans', label: 'Plans', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' },
  ] },
  { suffix: '/settings', label: 'Settings', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z', requiresPermission: 'manageSettings', children: [
    { suffix: '/data-health', label: 'Data Health', icon: 'M3 12h4l2-7 4 14 2-7h4' },
    { suffix: '/alerts', label: 'Alerts', icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' },
    { suffix: '/audit', label: 'Audit', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
  ] },
];

function SidebarIcon({ d }: { d: string }) {
  return (
    <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  );
}

export function DashboardLayout() {
  const { theme, setTheme } = useTheme();
  const { initials } = useUserName();
  const me = useMe();
  const can = useCan();

  // Show every hub by default. Only hide a route if the server has affirmatively
  // told us the user lacks the permission (me.user.id is populated). While
  // loading, show everything — RLS still enforces real security.
  const meLoaded = !!me.user.id;
  const visibleHubRoutes = HUB_ROUTES.filter(r => {
    // Server-resolved, so unlike founderOnly it shows while /v1/me loads (a full workspace never loses a nav item).
    if (r.hiddenForPartners && me.features?.partner === true) return false;
    if (r.founderOnly) {
      // Strict: never optimistically show founder-only routes. A customer
      // briefly seeing "PE KPIs" in their sidebar mid-load would be wrong.
      if (!meLoaded) return false;
      return isFounderEmail(me.user.email);
    }
    if (!r.requiresPermission) return true;
    if (!meLoaded) return true; // optimistic during load
    return can[r.requiresPermission];
  });
  const cmdPalette = useCommandPalette();
  useKeyboardShortcuts();
  // Keep integration data fresh while the user is actively in the app (sign-in,
  // tab refocus, periodic heartbeat). The 15-min server cron stays the offline
  // safety net. See lib/hooks/use-active-sync.
  useActiveSync();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = useBasePath();
  const contentRef = useRef<HTMLDivElement>(null);
  const wizardReopenRef = useRef<(() => void) | null>(null);
  const prevPath = useRef(location.pathname);
  useEffect(() => { trackPageView(location.pathname); }, [location.pathname]);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Embed mode — ?embed=true hides sidebar, header, and chrome for iframe embedding
  const isEmbed = new URLSearchParams(location.search).get('embed') === 'true';

  // Auto-redirect non-owners to their assigned platform (e.g. /sales)
  useEffect(() => {
    const PLATFORM_ROUTES: Record<string, string> = {
      sales: '/sales',
      tax: '/tax',
      sow: '/sow',
      clients: '/clients',
      finance: '/dashboard/revenue',
      operations: '/dashboard', // /dashboard/ops is founder-only internal tooling (403 for members)
      marketing: '/dashboard/marketing',
      executive: '/dashboard',
    };
    // Tax add-on off: a tax-assigned member must NOT be redirected to /tax
    // (TaxGate would bounce them back to /dashboard → infinite loop). Treat as
    // no assigned platform and leave them on the default dashboard.
    // Standalone product workspace (Sales / Clients / SOW Desk): the owner
    // dashboard does not exist for this account — send everyone (owner
    // included) to the workspace's home. Same rule as PlatformGuard.
    const standalone = standaloneHome({ workspaceKind: me.workspaceKind, homePath: me.homePath });
    if (me.user.id && standalone) {
      navigate(standalone, { replace: true });
      return;
    }
    // A partner's marketing-assigned member must not land on /dashboard/marketing: HiddenFromPartners would bounce
    // them back here and this effect would send them again, forever.
    const taxBlocked = (me.assignedPlatform === 'tax' && me.features?.tax !== true) || (me.assignedPlatform === 'sow' && me.features?.sow !== true) || (me.assignedPlatform === 'marketing' && me.features?.partner === true);
    if (
      me.assignedPlatform &&
      !taxBlocked &&
      me.role !== 'owner' &&
      me.role !== 'admin' &&
      me.user.id &&
      location.pathname === basePath
    ) {
      const target = PLATFORM_ROUTES[me.assignedPlatform];
      if (target && target !== basePath) {
        navigate(target, { replace: true });
      }
    }
  }, [me.assignedPlatform, me.workspaceKind, me.homePath, me.features?.tax, me.features?.sow, me.features?.partner, me.role, me.user.id, location.pathname, basePath, navigate]);

  // ── Voice Input ──────────────────────────────────────────────
  const voice = useSpeechRecognition({
    lang: 'en-US',
    silenceTimeout: 2500,
    onResult: (finalTranscript) => {
      // Stamp the send time so ChatPanel knows to auto-play TTS.
      (window as any).__waypointLastVoiceSend = Date.now();
      // Dispatch the transcript to ChatPanel for sending.
      window.dispatchEvent(new CustomEvent('waypoint:voice-send', { detail: finalTranscript }));
      // Reset voice state quickly so overlay dismisses.
      setTimeout(() => voice.reset(), 150);
    },
    onError: () => { /* VoiceOverlay shows errors — nothing else needed */ },
  });

  // Listen for Ctrl+Shift+V toggle from keyboard shortcuts
  useEffect(() => {
    const handler = () => voice.toggle();
    window.addEventListener('waypoint:toggle-voice', handler);
    return () => window.removeEventListener('waypoint:toggle-voice', handler);
  }, [voice.toggle]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close sidebar on route change (mobile)
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  useEffect(() => { closeSidebar(); }, [location.pathname, closeSidebar]);

  // Document title for the current route (WCAG 2.4.2).
  useEffect(() => {
    const suffix = location.pathname.replace(basePath, '') || '';
    document.title = pageTitle(HUB_ROUTES, suffix, 'Waypoint');
  }, [location.pathname, basePath]);


  // Page transition animation
  useEffect(() => {
    if (prevPath.current !== location.pathname && contentRef.current) {
      contentRef.current.style.opacity = '0';
      contentRef.current.style.transform = 'translateY(4px)';
      requestAnimationFrame(() => {
        if (contentRef.current) {
          contentRef.current.style.transition = 'opacity 150ms ease, transform 150ms ease';
          contentRef.current.style.opacity = '1';
          contentRef.current.style.transform = 'translateY(0)';
        }
      });
    }
    prevPath.current = location.pathname;
  }, [location.pathname]);

  return (
    <PaywallProvider>
    <div className="flex h-screen overflow-hidden bg-surface-0 text-ink">
      <a href="#main-content" className="skip-to-content">Skip to content</a>
      {/* Mobile sidebar overlay */}
      {!isEmbed && sidebarOpen && (
        <div className="fixed inset-0 z-overlay bg-black/50 backdrop-blur-xs md:hidden" onClick={closeSidebar} />
      )}

      {/* Sidebar — hidden in embed mode */}
      {!isEmbed && <aside className={clsx(
        'fixed inset-y-0 left-0 z-modal flex w-56 shrink-0 flex-col border-r border-border bg-surface-0 transition-transform duration-200 md:relative md:translate-x-0',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        <div className="flex h-14 items-center gap-2.5 px-5">
          <WaypointMark size={32} rounded="rounded-lg" />
          <span className="text-sm font-semibold tracking-tight text-ink">Waypoint</span>
          {/* Close button on mobile */}
          <button aria-label="Close navigation" onClick={closeSidebar} className="ml-auto rounded-lg p-2 text-ink-muted hover:bg-surface-2 md:hidden min-h-[44px] min-w-[44px] flex items-center justify-center">
            <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>

        <WorkspaceSwitcher />
        <PlatformSwitcher />

        {/* Search pill */}
        <div className="px-3 mb-2">
          <button onClick={() => { cmdPalette.open(); closeSidebar(); }} className="flex w-full items-center gap-2 rounded-lg border border-border bg-surface-1 px-3 py-2 text-2xs text-ink-dim transition-colors hover:border-border-strong hover:bg-surface-2 min-h-[44px] md:min-h-0">
            <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
            Search or jump to...
            <span className="ml-auto rounded-sm border border-border px-1 py-0.5 text-2xs font-mono text-ink-dim hidden sm:inline">⌘K</span>
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-1 space-y-0.5">
          {visibleHubRoutes.map(item => {
            const childActive = item.children?.some(c => location.pathname === `${basePath}${c.suffix}`);
            const parentPath = `${basePath}${item.suffix}`;
            const isParentActive = item.suffix === '' ? location.pathname === parentPath : location.pathname.startsWith(parentPath);
            const expanded = isParentActive || !!childActive;

            return (
              <div key={item.suffix}>
                <NavLink
                  to={parentPath}
                  end={item.suffix === ''}
                  className={({ isActive }) => clsx(
                    'group relative flex items-center gap-2.5 rounded-lg px-3 py-2 text-xs font-medium transition-all duration-150 min-h-[44px] md:min-h-0',
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-ink-muted hover:bg-surface-2 hover:text-ink'
                  )}
                >
                  {({ isActive }) => (
                    <>
                      {isActive && <div className="absolute left-0 top-1/2 h-5 w-[3px] -translate-x-0.5 -translate-y-1/2 rounded-r-full bg-primary" />}
                      <SidebarIcon d={item.icon} />
                      {item.label}
                      {item.children && item.children.length > 0 && (
                        <svg aria-hidden="true" className={clsx('ml-auto w-3 h-3 text-ink-dim transition-transform', expanded && 'rotate-90')} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
                      )}
                    </>
                  )}
                </NavLink>
                {item.children && expanded && (
                  <div className="ml-5 mt-0.5 space-y-0.5 border-l border-border/50 pl-2">
                    {item.children.map(child => (
                      <NavLink
                        key={child.suffix}
                        to={`${basePath}${child.suffix}`}
                        className={({ isActive }) => clsx(
                          'group relative flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-2xs font-medium transition-all duration-150 min-h-[36px] md:min-h-0',
                          isActive
                            ? 'bg-primary/10 text-primary'
                            : 'text-ink-muted hover:bg-surface-2 hover:text-ink'
                        )}
                      >
                        {({ isActive }) => (
                          <>
                            {isActive && <div className="absolute left-0 top-1/2 h-4 w-[2px] -translate-x-0.5 -translate-y-1/2 rounded-r-full bg-primary" />}
                            <SidebarIcon d={child.icon} />
                            {child.label}
                          </>
                        )}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Theme toggle */}
        <div className="border-t border-border px-3 py-3">
          <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-2xs text-ink-dim transition-colors hover:bg-surface-2 hover:text-ink min-h-[44px] md:min-h-0">
            {theme === 'dark' ? (
              <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="5" /><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" /></svg>
            ) : (
              <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" /></svg>
            )}
            {theme === 'dark' ? 'Light mode' : 'Dark mode'}
          </button>
        </div>
      </aside>}

      {/* Main area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Top bar — hidden in embed mode */}
        {!isEmbed && <header className="flex h-14 shrink-0 items-center justify-between border-b border-border px-3 md:px-5">
          {/* Hamburger (mobile only) */}
          <button aria-label="Open navigation" aria-expanded={sidebarOpen} onClick={() => setSidebarOpen(true)} className="mr-2 rounded-lg p-2 text-ink-muted hover:bg-surface-2 md:hidden min-h-[44px] min-w-[44px] flex items-center justify-center">
            <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 12h18M3 6h18M3 18h18" /></svg>
          </button>

          <div className="flex-1" />

          {/* Right controls */}
          <div className="flex items-center gap-1 md:gap-2">
            <NotificationCenter />
            <button
              onClick={() => voice.supported ? voice.toggle() : window.dispatchEvent(new CustomEvent('waypoint:open-chat'))}
              className={clsx(
                'rounded-lg p-2 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center',
                voice.listening
                  ? 'bg-primary/15 text-primary animate-pulse'
                  : 'text-ink-muted hover:bg-surface-2 hover:text-ink',
              )}
              title={voice.supported ? (voice.listening ? 'Stop listening' : 'Voice input (Ctrl+Shift+V)') : 'Waypoint AI'}
            >
              <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="1" width="6" height="11" rx="3" />
                <path d="M5 10v1a7 7 0 0 0 14 0v-1" />
                <line x1="12" y1="18" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
            </button>
            <div className="ml-1 flex items-center gap-2">
              <ScopePicker />
              <span
                className="rounded-full bg-surface-2 px-2 py-0.5 text-2xs font-semibold uppercase tracking-wider text-ink-dim hidden sm:inline-block"
                title={`Role: ${ROLE_LABEL[me.role]} — ${me.account.name ?? 'workspace'}`}
              >
                {ROLE_LABEL[me.role]}
              </span>
              <div
                className="h-8 w-8 md:h-7 md:w-7 rounded-full bg-linear-to-br from-primary to-primary-bright flex items-center justify-center text-2xs font-semibold text-white cursor-pointer"
                onClick={() => navigate(can.manageSettings ? `${basePath}/settings` : `${basePath}/audit`)}
                title={`${me.user.email || 'Account'} (${ROLE_LABEL[me.role]})`}
              >
                {initials}
              </div>
            </div>
          </div>
        </header>}

        {/* Content */}
        {/* One banner at a time. Each child decides whether to render; the
            first that does wins and the rest are suppressed by .banner-slot
            (index.css). Order = priority. */}
        {!isEmbed && (
          <div className="banner-slot">
            <ImpersonationBanner />
            <DemoBanner />
            <TrialBanner mode="urgent" />
            <DataFreshnessBanner mode="alert" />
            <ContinueSetupBanner onReopen={() => wizardReopenRef.current?.()} />
            <ActivationNudge />
            <TrialBanner mode="countdown" />
            <IndustryCtaBanner />
          </div>
        )}
        {!isEmbed && <DataFreshnessBanner mode="status" />}
        <main id="main-content" className="flex-1 overflow-y-auto">
          <div ref={contentRef} className="pb-16 md:pb-0">
            {/* pb-16 leaves room for the mobile bottom nav */}
            <div key={location.pathname} className="animate-fade-in">
              {/* Per-page boundary: a crash in one route shows a contained
                  fallback (nav shell survives) and resets on navigation via the
                  parent's key={pathname}. Covers every nested dashboard route. */}
              <RouteErrorBoundary>
                <Outlet />
              </RouteErrorBoundary>
            </div>
          </div>
        </main>
      </div>

      {!isEmbed && (
        <Suspense fallback={null}>
          <CommandPalette isOpen={cmdPalette.isOpen} onClose={cmdPalette.close} onOpenAgent={() => { cmdPalette.close(); window.dispatchEvent(new CustomEvent('waypoint:open-chat')); }} />
        </Suspense>
      )}
      {!isEmbed && <ShortcutsHelp />}
      {!isEmbed && (
        <Suspense fallback={null}>
          <ChatPanel />
        </Suspense>
      )}
      {!isEmbed && <OnboardingWizard reopenRef={wizardReopenRef} />}
      {!isEmbed && <TrialRecapModal />}
      {!isEmbed && <MobileBottomNav />}
      <Suspense fallback={null}>
        <VoiceOverlay
          status={voice.status}
          transcript={voice.transcript}
          interimTranscript={voice.interimTranscript}
          error={voice.error}
          onCancel={voice.reset}
        />
      </Suspense>
    </div>
    </PaywallProvider>
  );
}
